import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle, Clock, Loader2, Receipt } from "lucide-react";

import { useGetReceiptQuery } from "../../api/receiptApi";
import { formatCurrency, formatDate } from "../../../../utils/formatters";

/**
 * Where OnePay sends the browser back after a payment that settles SEVERAL
 * invoices at once (`meta.type = dues_bulk`) — a member clearing all their
 * dues, or a firm clearing its own.
 *
 * Those payments have no single invoice to return to, so the callback redirects
 * to /payment-processing/{reference} (OnePayWebhookController). Nothing was
 * mounted on that path, so every bulk payment ended on the portal's
 * "page not found" — the money had been taken and the job had settled it, and
 * the payer was told the page did not exist.
 *
 * The reference is the only thing the gateway hands back, so the receipt is
 * asked for by reference and polled: settlement happens in a queued job, which
 * is usually a second or two behind the redirect. A receipt means the payment
 * is confirmed; no receipt yet means exactly that, and is reported as such
 * rather than as a failure.
 */
const POLL_MS = 3000;
const GIVE_UP_AFTER = 20; // ~60 seconds

export default function DuesPaymentReturnPage() {
  const { reference = "" } = useParams<{ reference: string }>();

  const { data: receipt, isError } = useGetReceiptQuery(reference, {
    skip: !reference,
    pollingInterval: POLL_MS,
  });

  // Tick count, so the page stops promising "any moment now" forever.
  const [ticks, setTicks] = useState(0);
  useEffect(() => {
    if (receipt) return;
    const t = setInterval(() => setTicks((n) => n + 1), POLL_MS);
    return () => clearInterval(t);
  }, [receipt]);

  // Where to send them on. A firm's account has nowhere else to be; anyone
  // else came from their own invoices.
  const whois = (() => {
    try {
      const raw = localStorage.getItem("logbook_auth_user_v2");
      return raw ? String(JSON.parse(raw)?.whois ?? "").toLowerCase() : "";
    } catch {
      return "";
    }
  })();
  const onward = whois === "firm" ? "/my-firm/invoices" : "/my-invoices";

  if (receipt) {
    return (
      <div className="max-w-xl mx-auto p-4 md:p-6">
        <div className="bg-white border rounded-2xl p-8 text-center space-y-4">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Payment confirmed</h1>
            <p className="text-sm text-gray-500 mt-1">
              {formatCurrency(receipt.amount)} received
              {receipt.date ? ` on ${formatDate(receipt.date)}` : ""}.
            </p>
          </div>

          <div className="rounded-xl border bg-gray-50 p-4 text-left space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400 text-[10px] font-black uppercase tracking-widest">
                Receipt no
              </span>
              <span className="font-mono font-semibold text-gray-800">
                {receipt.receipt_number ?? "—"}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400 text-[10px] font-black uppercase tracking-widest">
                Reference
              </span>
              <span className="font-mono text-gray-600 text-xs break-all">{reference}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
            {receipt.payment_id && (
              <Link
                to={`/receipt/${receipt.payment_id}`}
                className="px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold inline-flex items-center gap-2"
              >
                <Receipt className="w-4 h-4" /> View receipt
              </Link>
            )}
            <Link
              to={onward}
              className="px-5 py-2.5 rounded-xl border text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Back to invoices
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const givenUp = ticks >= GIVE_UP_AFTER;

  return (
    <div className="max-w-xl mx-auto p-4 md:p-6">
      <div className="bg-white border rounded-2xl p-8 text-center space-y-4">
        {givenUp ? (
          <Clock className="w-12 h-12 text-amber-500 mx-auto" />
        ) : (
          <Loader2 className="w-12 h-12 text-emerald-500 mx-auto animate-spin" />
        )}

        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {givenUp ? "Still being confirmed" : "Confirming your payment…"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {givenUp
              ? "The bank has your money; the Institute's confirmation has not come back yet. " +
                "Your invoices will update by themselves once it does — keep the reference below, " +
                "and quote it to the secretariat if the balance has not changed by tomorrow."
              : "We are waiting for the gateway to confirm the transaction. This takes a few " +
                "seconds; please do not close this page or pay again."}
          </p>
        </div>

        <div className="rounded-xl border bg-gray-50 p-4 text-left">
          <div className="text-gray-400 text-[10px] font-black uppercase tracking-widest">
            Reference
          </div>
          <div className="font-mono text-xs text-gray-700 break-all mt-1">{reference || "—"}</div>
        </div>

        {/* `isError` here is the receipt endpoint's own "not yet" (404), not a
            broken request — so it is deliberately not reported as a failure. */}
        {givenUp && (
          <Link
            to={onward}
            className="inline-block px-5 py-2.5 rounded-xl border text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Back to invoices
          </Link>
        )}
        {!givenUp && isError && (
          <p className="text-[11px] text-gray-400">Waiting for the first confirmation…</p>
        )}
      </div>
    </div>
  );
}
